import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Loader2, Mail } from 'lucide-react';
import { gmailEntityAssignmentAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function GmailMailboxEntityMappingPanel() {
  const [mappings, setMappings] = useState([]);
  const [unmappedAddresses, setUnmappedAddresses] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingMailbox, setSavingMailbox] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [mappingsRes, entitiesRes] = await Promise.all([
        gmailEntityAssignmentAPI.getMailboxMappings(),
        gmailEntityAssignmentAPI.getEntities(),
      ]);
      setMappings(mappingsRes.data?.mappings || []);
      setUnmappedAddresses(mappingsRes.data?.unmapped_addresses || []);
      setEntities(entitiesRes.data?.entities || []);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAssign = async (mailbox, entityId) => {
    if (!entityId) return;
    setSavingMailbox(mailbox);
    try {
      await gmailEntityAssignmentAPI.setMailboxMapping(mailbox, entityId);
      toast.success(`Mapped ${mailbox} to entity`);
      load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingMailbox(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Gmail Mailbox → Entity Mappings</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Recipient addresses that have received Gmail invoices. Map an address to an entity so future
          invoices sent there resolve automatically (tier 1 of the deterministic resolver).
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <div className="space-y-2 text-xs">
            {mappings.length > 0 && (
              <div className="space-y-1">
                <p className="font-medium">Mapped</p>
                {mappings.map((m) => (
                  <div key={m.mailbox} className="flex items-center justify-between border rounded px-2 py-1">
                    <span className="font-mono">{m.mailbox}</span>
                    <select
                      className="border rounded text-xs px-1 py-0.5 bg-background"
                      value={m.entity_id}
                      disabled={savingMailbox === m.mailbox}
                      onChange={(e) => handleAssign(m.mailbox, e.target.value)}
                      data-testid={`mailbox-mapping-select-${m.mailbox}`}
                    >
                      {entities.map((ent) => (
                        <option key={ent.id} value={ent.id}>{ent.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
            {unmappedAddresses.length > 0 ? (
              <div className="space-y-1">
                <p className="font-medium">Unmapped ({unmappedAddresses.length})</p>
                {unmappedAddresses.map((addr) => (
                  <div key={addr} className="flex items-center justify-between border rounded px-2 py-1">
                    <span className="font-mono">{addr}</span>
                    <select
                      className="border rounded text-xs px-1 py-0.5 bg-background"
                      defaultValue=""
                      disabled={savingMailbox === addr}
                      onChange={(e) => handleAssign(addr, e.target.value)}
                      data-testid={`mailbox-unmapped-select-${addr}`}
                    >
                      <option value="" disabled>Assign entity...</option>
                      {entities.map((ent) => (
                        <option key={ent.id} value={ent.id}>{ent.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            ) : (
              mappings.length === 0 && <p className="text-muted-foreground">No Gmail recipient addresses seen yet.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
